import React from "react";

const HOMES = ["", "ADONAI", "EAOLHIM", "SHALOM", "JIREH", "PERIS", "EMMANUEL", "ADMINISTRATION"];
const GENDERS = ["", "Male", "Female"];
const CLASSIFICATIONS = ["", "Direct Care Worker", "Contract Care Worker"];
const ACCESS_ROLES = ["CEO", "Project Manager", "COO", "Administration", "House Manager", "Staff"];
const DIGEST_ELIGIBLE_ROLES = new Set(["CEO", "Project Manager", "COO", "House Manager"]);
const DIGEST_ORG_ROLES = new Set(["CEO", "Project Manager", "COO"]);

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="text-small-caps">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500/30" />
    </label>
  );
}

function SelectField({ label, value, options, onChange, placeholder }) {
  const opts = options.map((o) => typeof o === "string" ? { v: o, l: o || (placeholder || "—") } : o);
  return (
    <label className="block">
      <span className="text-small-caps">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2">
        {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function MultiHomeField({ label, value, options, onChange }) {
  const selected = value || [];
  const toggle = (h) => {
    const next = selected.includes(h) ? selected.filter(x => x !== h) : [...selected, h];
    onChange(next);
  };
  return (
    <label className="block col-span-2">
      <span className="text-small-caps">{label}</span>
      <div className="mt-1 flex flex-wrap gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid="multi-home-field">
        {options.map((h) => {
          const on = selected.includes(h);
          return (
            <button
              type="button"
              key={h}
              onClick={() => toggle(h)}
              data-testid={`home-chip-${h}${on ? "-on" : ""}`}
              className={
                "px-2.5 py-1 rounded-full text-xs font-semibold border transition " +
                (on
                  ? "bg-sky-700 text-white border-sky-700"
                  : "bg-white text-slate-600 border-slate-200 hover:border-sky-400")
              }
            >
              {h}
            </button>
          );
        })}
      </div>
      {selected.length > 1 && <p className="text-[12.5px] text-slate-500 mt-1">{selected.length} homes assigned · primary: {selected[0]}</p>}
    </label>
  );
}

/**
 * Modal used for BOTH create-new and edit-existing user flows.
 * Behavior:
 *   - mode="create": Email + Password are required; testid wraps with create-user-modal.
 *   - mode="edit":   Email is read-only (managed via the Edit-Email modal); password is optional.
 *
 * Parent controls open/close via `value` (truthy = open). `value` is the editable user dict.
 */
export default function UserFormModal({ mode, value, onChange, onCancel, onSubmit,
                                       canManageDigestPermission = false,
                                       currentUserId = null }) {
  if (!value) return null;
  const isCreate = mode === "create";
  const modalTestId = isCreate ? "create-user-modal" : "edit-user-modal";
  const submitTestId = isCreate ? "create-user-save-btn" : "edit-user-save-btn";

  // Digest-scope preview (read-only). Recomputes from live role + homes; editing
  // the display does NOT grant access — the source of truth is the backend
  // resolver at send time.
  const digestEligible = DIGEST_ELIGIBLE_ROLES.has(value.access_role);
  const homesForScope = value.assigned_homes && value.assigned_homes.length
    ? value.assigned_homes
    : (value.assigned_home ? [value.assigned_home] : []);
  let scopeLabel = "Not eligible for digest";
  if (digestEligible) {
    if (DIGEST_ORG_ROLES.has(value.access_role)) {
      scopeLabel = "Org-wide";
    } else if (value.access_role === "House Manager") {
      scopeLabel = homesForScope.length
        ? `Homes: ${homesForScope.filter(h => h && h !== "ADMINISTRATION").join(", ") || "—"}`
        : "House Manager with no assigned homes — will be skipped";
    }
  }
  // Self-edit lockout for the digest toggle (backend rejects too — this is the UX guard).
  const editingSelf = !isCreate && currentUserId && value.user_id === currentUserId;
  const digestToggleDisabled = !canManageDigestPermission || editingSelf || !digestEligible;
  const digestToggleTip = editingSelf
    ? "You cannot grant yourself digest access."
    : (!canManageDigestPermission
        ? "Only CEO, Project Manager, or COO can change this."
        : (!digestEligible
            ? "This role is not eligible to receive the monthly KPI digest."
            : "Send this user the monthly KPI email digest on the 1st of each month, restricted to homes they have access to."));
  const digestValue = digestEligible ? !!value.receive_monthly_kpi_digest : false;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="nv-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid={modalTestId}>
        <h2 className="text-h2">{isCreate ? "Add new user" : "Edit user"}</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {isCreate ? "User will be notified to change password on first login." : value.email}
        </p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Field label={isCreate ? "Full name *" : "Full name"} value={value.full_name} onChange={(v) => onChange({ ...value, full_name: v })} />
          {isCreate && <Field label="Email *" type="email" value={value.email} onChange={(v) => onChange({ ...value, email: v })} />}
          <Field label="Job Title" value={value.job_title || value.role_title} onChange={(v) => onChange({ ...value, job_title: v, role_title: v })} />
          <SelectField label={isCreate ? "Access role *" : "Access role"} value={value.access_role} options={ACCESS_ROLES} onChange={(v) => onChange({ ...value, access_role: v })} />
          {value.access_role === "House Manager" ? (
            <MultiHomeField
              label="Assigned Homes (multi)"
              value={value.assigned_homes || (value.assigned_home ? [value.assigned_home] : [])}
              options={HOMES.filter(h => h)}
              onChange={(arr) => onChange({ ...value, assigned_homes: arr, assigned_home: arr[0] || "" })}
            />
          ) : (
            <SelectField label="Home" value={value.assigned_home} options={HOMES} onChange={(v) => onChange({ ...value, assigned_home: v, assigned_homes: v ? [v] : [] })} />
          )}
          <SelectField label="Staff Classification" value={value.staff_classification} options={CLASSIFICATIONS} placeholder="Select Classification" onChange={(v) => onChange({ ...value, staff_classification: v })} />
          <SelectField label="Gender" value={value.gender} options={GENDERS} placeholder="Select Gender" onChange={(v) => onChange({ ...value, gender: v })} />
          <Field label={isCreate ? "Password *" : "New password (optional)"} type="password" value={value.password} onChange={(v) => onChange({ ...value, password: v })} />
          <SelectField label="Active" value={value.active_status ? "true" : "false"} options={[{v:"true",l:"Active"},{v:"false",l:"Inactive"}]} onChange={(v) => onChange({ ...value, active_status: v === "true" })} />
        </div>

        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="digest-permission-block">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-small-caps text-slate-700">Monthly KPI Email Digest</div>
              <p className="text-xs text-slate-500 mt-0.5" title={digestToggleTip}>
                {digestToggleTip}
              </p>
            </div>
            <label className={"inline-flex items-center gap-2 text-sm " + (digestToggleDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer")}>
              <input
                type="checkbox"
                checked={digestValue}
                disabled={digestToggleDisabled}
                onChange={(e) => onChange({ ...value, receive_monthly_kpi_digest: e.target.checked })}
                data-testid="digest-permission-toggle"
                className="h-4 w-4 rounded border-slate-300 text-sky-700 focus:ring-sky-500"
              />
              <span className="font-medium text-slate-700">Receive digest</span>
            </label>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[12.5px] uppercase tracking-wide text-slate-500 font-semibold">Digest scope</span>
            <span
              className={"px-2 py-0.5 rounded text-[12.5px] font-semibold border " +
                (digestEligible
                  ? (DIGEST_ORG_ROLES.has(value.access_role) ? "bg-sky-50 border-sky-200 text-sky-700" : "bg-emerald-50 border-emerald-200 text-emerald-700")
                  : "bg-slate-100 border-slate-200 text-slate-500")}
              data-testid="digest-scope-display"
            >
              {scopeLabel}
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={onSubmit} className="px-4 py-2 rounded-md text-sm font-semibold bg-sky-800 hover:bg-sky-900 text-white" data-testid={submitTestId}>
            {isCreate ? "Create user" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
